/**
 * BlogLayout Component
 * Public layout for blog pages (no auth required)
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Moon, Sun } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { cn } from '../../lib/utils';
import { LanguageSwitcher } from '../../components/blog';
import Footer from '../../components/footer/Footer';

// Theme hook that integrates with app's existing theme system
const useTheme = () => {
  const [theme, setTheme] = useState(() => {
    if (typeof window === 'undefined') return 'dark';

    try {
      const saved = localStorage.getItem('userPreferences');
      if (saved && saved.startsWith('{')) {
        const prefs = JSON.parse(saved);
        return prefs.theme || 'dark';
      }
    } catch (e) {
      // Ignore parse errors
    }
    return 'dark';
  });

  const applyTheme = useCallback((newTheme) => {
    const root = document.documentElement;
    const effectiveTheme =
      newTheme === 'system'
        ? window.matchMedia('(prefers-color-scheme: dark)').matches
          ? 'dark'
          : 'light'
        : newTheme;

    if (effectiveTheme === 'dark') {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
  }, []);

  useEffect(() => {
    applyTheme(theme);
  }, [theme, applyTheme]);

  const toggleTheme = useCallback(() => {
    const newTheme = theme === 'dark' ? 'light' : 'dark';
    setTheme(newTheme);

    // Save to localStorage in the same format as the app
    try {
      const saved = localStorage.getItem('userPreferences');
      let prefs = { theme: newTheme };
      if (saved && saved.startsWith('{')) {
        prefs = { ...JSON.parse(saved), theme: newTheme };
      }
      localStorage.setItem('userPreferences', JSON.stringify(prefs));
    } catch (e) {
      localStorage.setItem(
        'userPreferences',
        JSON.stringify({ theme: newTheme })
      );
    }

    // Use global apply function if available
    if (window.__applyTheme) {
      window.__applyTheme(newTheme);
    }
  }, [theme]);

  // Resolve effective theme for icon display
  const effectiveTheme =
    theme === 'system'
      ? typeof window !== 'undefined' &&
        window.matchMedia('(prefers-color-scheme: dark)').matches
        ? 'dark'
        : 'light'
      : theme;

  return { theme: effectiveTheme, toggleTheme };
};

const BlogLayout = ({ children }) => {
  const { t } = useTranslation();
  const { theme, toggleTheme } = useTheme();

  return (
    <div className="min-h-screen bg-neutral-50 dark:bg-neutral-950">
      {/* Header */}
      <header className="sticky top-0 z-50 border-b border-neutral-200 bg-white/80 backdrop-blur-lg dark:border-neutral-800 dark:bg-neutral-900/80">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2">
            <img
              src="/rely_transparente.png"
              alt="Rely"
              className="hidden h-12 w-auto dark:block"
            />
            <img
              src="/rely-logo.png"
              alt="Rely"
              className="block h-18 w-auto dark:hidden"
            />
          </Link>

          {/* Right Section */}
          <div className="flex items-center gap-3">
            {/* Language Switcher */}
            <LanguageSwitcher />

            {/* Theme Toggle */}
            <motion.button
              onClick={toggleTheme}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className={cn(
                'rounded-full p-2',
                'bg-neutral-100 text-neutral-600',
                'dark:bg-neutral-800 dark:text-neutral-400',
                'hover:bg-neutral-200 dark:hover:bg-neutral-700',
                'transition-colors duration-200'
              )}
              aria-label={t('blog.toggleTheme', 'Cambiar tema')}
            >
              {theme === 'dark' ? (
                <Sun className="h-5 w-5" strokeWidth={2.5} />
              ) : (
                <Moon className="h-5 w-5" strokeWidth={2.5} />
              )}
            </motion.button>

            {/* CTA Button */}
            <Link
              to="/auth/sign-up/default"
              className={cn(
                'hidden rounded-full px-4 py-2 text-sm font-medium sm:inline-flex',
                'bg-primary-500 text-white',
                'hover:bg-primary-600 hover:text-white',
                'transition-colors duration-200'
              )}
            >
              {t('blog.startNow', 'Empezar ahora')}
            </Link>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6 sm:py-12 lg:px-8">
        {children}
      </main>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default BlogLayout;
